import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ChatStateProps, Message } from "../interfaces/chat";

const STORAGE_KEY = 'linze_chat_conversation';
const CASE_ID_KEY = 'linze_chat_case_id';
const FEEDBACK_KEY = 'linze_chat_feedback';

const generateCaseId = (): string => {
    return `${Date.now().toString(16)}${Math.random().toString(36).substr(2, 16)}`;
};

const loadCaseIdFromStorage = (): string => {
    try {
        const stored = sessionStorage.getItem(CASE_ID_KEY);
        if (stored) return stored;
    } catch (error) {
        console.error('Error loading caseId from sessionStorage:', error);
    }
    const newCaseId = generateCaseId();
    try {
        sessionStorage.setItem(CASE_ID_KEY, newCaseId);
    } catch (error) {
        console.error('Error saving caseId to sessionStorage:', error);
    }
    return newCaseId;
};

const loadMessagesFromStorage = (): Message[] => {
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error('Error loading chat from sessionStorage:', error);
    }
    return [];
};

const saveMessagesToStorage = (messages: Message[]) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
        console.error('Error saving chat to sessionStorage:', error);
    }
};

const loadFeedbackFromStorage = (): { sent: boolean; liked: boolean | null } => {
    try {
        const stored = sessionStorage.getItem(FEEDBACK_KEY);
        if (stored) return JSON.parse(stored);
    } catch {}
    return { sent: false, liked: null };
};

const saveFeedbackToStorage = (sent: boolean, liked: boolean | null) => {
    try {
        sessionStorage.setItem(FEEDBACK_KEY, JSON.stringify({ sent, liked }));
    } catch {}
};

const feedback = loadFeedbackFromStorage();

const initialState: ChatStateProps = {
    isOpen: false,
    messages: loadMessagesFromStorage(),
    fetching: "unknown",
    error: "",
    isTyping: false,
    caseId: loadCaseIdFromStorage(),
    feedbackSent: feedback.sent,
    feedbackLiked: feedback.liked,
};

const chatSlice = createSlice({
    name: "chat",
    initialState,
    reducers: {
        toggleChat: (state) => {
            state.isOpen = !state.isOpen;
        },
        openChat: (state) => {
            state.isOpen = true;
        },
        closeChat: (state) => {
            state.isOpen = false;
        },
        sendMessageRequest: (state) => {
            state.fetching = "loading";
            state.error = "";
            state.isTyping = true;
        },
        sendMessageSuccess: (state, action: PayloadAction<Message>) => {
            state.fetching = "success";
            state.messages.push(action.payload);
            state.isTyping = false;
            saveMessagesToStorage(state.messages);
        },
        sendMessageFailure: (state, action: PayloadAction<string>) => {
            state.fetching = "failure";
            state.error = action.payload;
            state.isTyping = false;
        },
        addUserMessage: (state, action: PayloadAction<Message>) => {
            state.messages.push(action.payload);
            saveMessagesToStorage(state.messages);
        },
        clearMessages: (state) => {
            state.messages = [];
            state.fetching = 'unknown';
            state.error = '';
            state.feedbackSent = false;
            state.feedbackLiked = null;
            sessionStorage.removeItem(STORAGE_KEY);
            sessionStorage.removeItem(FEEDBACK_KEY);
            const newCaseId = generateCaseId();
            state.caseId = newCaseId;
            try {
                sessionStorage.setItem(CASE_ID_KEY, newCaseId);
            } catch (error) {
                console.error('Error saving caseId to sessionStorage:', error);
            }
        },
        setTyping: (state, action: PayloadAction<boolean>) => {
            state.isTyping = action.payload;
        },
        sendFeedbackRequest: (state) => {
            // no-op, se puede usar para loading si se quiere
        },
        sendFeedbackSuccess: (state, action: PayloadAction<boolean>) => {
            state.feedbackSent = true;
            state.feedbackLiked = action.payload;
            saveFeedbackToStorage(true, action.payload);
        },
        sendFeedbackFailure: (state) => {
            // Aún así marcamos como enviado para no molestar al usuario
            state.feedbackSent = true;
            saveFeedbackToStorage(true, state.feedbackLiked);
        },
    },
});

export const {
    toggleChat,
    openChat,
    closeChat,
    sendMessageRequest,
    sendMessageSuccess,
    sendMessageFailure,
    addUserMessage,
    clearMessages,
    setTyping,
    sendFeedbackRequest,
    sendFeedbackSuccess,
    sendFeedbackFailure,
} = chatSlice.actions;

export default chatSlice.reducer;
